import { httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { functions, db, auth } from '../firebase/config';

const SIGNUP_REQUESTS_COLLECTION = 'signupRequests';

/**
 * Whether the currently signed-in user is an admin (via the `admin` custom claim).
 * Custom claims only appear after a token refresh, so we force-refresh once.
 */
export async function checkIsAdmin() {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    const tokenResult = await user.getIdTokenResult(true);
    return tokenResult.claims.admin === true;
  } catch (e) {
    console.error('Failed to read admin claim:', e);
    return false;
  }
}

/**
 * List signup requests by status (admin-only; enforced by Firestore rules).
 */
export async function listSignupRequests(status = 'pending') {
  const constraints = [where('status', '==', status), limit(200)];
  let q = query(collection(db, SIGNUP_REQUESTS_COLLECTION), ...constraints, orderBy('createdAt', 'desc'));
  let snap;
  try {
    snap = await getDocs(q);
  } catch (err) {
    // Fallback without orderBy if a composite index is missing.
    if (err.code === 'failed-precondition') {
      q = query(collection(db, SIGNUP_REQUESTS_COLLECTION), ...constraints);
      snap = await getDocs(q);
    } else {
      throw err;
    }
  }
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  return rows;
}

/**
 * Approve a request with the chosen access controls (array of
 * [chain, token, application, userAddress] tuples; empty = no access).
 * Long timeout: approval also backfills historical auditorRequestGrants.
 */
export async function approveSignupRequest(requestId, accessControls = [], name = null) {
  const callable = httpsCallable(functions, 'approveSignupRequest', { timeout: 540_000 });
  const result = await callable({ requestId, accessControls, name });
  return result.data;
}

/**
 * Reject a request. Optionally notify the requester by email.
 */
export async function rejectSignupRequest(requestId, reason = null, notify = true) {
  const callable = httpsCallable(functions, 'rejectSignupRequest');
  const result = await callable({ requestId, reason, notify });
  return result.data;
}
